// Powerful motion (merchant, 19 Oct 2026): GSAP + ScrollTrigger + Lenis, loaded from jsDelivr only when the shopper
// allows motion. Inside the theme editor only smooth scroll (Lenis) is skipped so editing scrolls normally. If the libraries fail to load, the site works as before.
// - Lenis: smooth page scrolling (mouse wheel / trackpad; phones keep their native scroll).
// - Section headings: words rise into place one after another as the heading scrolls into view.
// - Parallax (hero banner stays still so its sale text is never cut): collection page cover, Shop the Look videos and Why cards drift at different speeds.
const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const designMode = window.Shopify && window.Shopify.designMode;

const load = (src) => new Promise((resolve, reject) => {
  const s = document.createElement('script');
  s.src = src;
  s.async = true;
  s.onload = resolve;
  s.onerror = reject;
  document.head.appendChild(s);
});

// Wrap each word of a heading in a mask so it can slide up. Child elements (like the orange word) move as one piece.
function splitWords(el) {
  if (el.dataset.split) return [];
  el.dataset.split = '1';
  const words = [];
  const wrap = (node) => {
    const outer = document.createElement('span');
    outer.className = 'hbr-w';
    const inner = document.createElement('span');
    inner.className = 'hbr-wi';
    outer.appendChild(inner);
    node.replaceWith(outer);
    inner.appendChild(node);
    words.push(inner);
  };
  [...el.childNodes].forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const parts = node.textContent.split(/(\s+)/);
      const frag = document.createDocumentFragment();
      parts.forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
        const t = document.createTextNode(part);
        frag.appendChild(t);
      });
      const created = [...frag.childNodes];
      node.replaceWith(frag);
      created.forEach((t) => { if (t.textContent.trim()) wrap(t); });
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      wrap(node);
    }
  });
  return words;
}

async function run() {
  await load('https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js');
  await load('https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/ScrollTrigger.min.js');
  const { gsap, ScrollTrigger } = window;
  if (!gsap || !ScrollTrigger) return;
  gsap.registerPlugin(ScrollTrigger);
  document.documentElement.classList.add('hbr-gsap');

  // Smooth scroll
  if (!designMode) try {
    await load('https://cdn.jsdelivr.net/npm/lenis@1.1.13/dist/lenis.min.js');
    if (window.Lenis) {
      const lenis = new window.Lenis({
        duration: 1.15,
        anchors: true,
        prevent: (node) => !!node.closest?.('[data-carousel], [data-carousel-track], [data-slideshow-track], dialog, [role="dialog"], .hbr-ask, .hbr-rvcar, .hbr-dirc-row, [data-lenis-prevent]'),
      });
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add((time) => lenis.raf(time * 1000));
      gsap.ticker.lagSmoothing(0);
      window.hbrLenis = lenis;
    }
  } catch (e) { /* smooth scroll is optional */ }

  // Headings stay still (merchant, 19 Oct 2026: text should not move up and down).
  // Parallax
  document.querySelectorAll('.hbr-colhero__media img').forEach((img) => {
    gsap.fromTo(img, { yPercent: -8, scale: 1.15 }, { yPercent: 8, scale: 1.15, ease: 'none', scrollTrigger: { trigger: img.closest('.hbr-colhero__media'), start: 'top bottom', end: 'bottom top', scrub: true } });
  });
  document.querySelectorAll('[data-reels] .hbr-reel-slide').forEach((card, i) => {
    gsap.fromTo(card, { y: i % 2 ? 50 : 10 }, { y: i % 2 ? -30 : -10, ease: 'none', scrollTrigger: { trigger: card.closest('[data-reels]') || card, start: 'top bottom', end: 'bottom top', scrub: 0.6 } });
  });

  window.addEventListener('load', () => ScrollTrigger.refresh());
}

if (!reduce) {
  const go = () => run().catch(() => document.documentElement.classList.remove('hbr-gsap'));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', go);
  else go();
}
